import { Alert, Badge, Button, Grid, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconLock, IconUser, IconUsers } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CSSProperties } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { SPREAD_MODE_LABELS, type SpreadDetail, type SpreadGuidance } from '@creativitas/shared';
import { AssetImage } from '@/components/ui/AssetImage';
import { PageHeader } from '@/components/ui/PageHeader';
import { ErrorState, LoadingState } from '@/components/ui/StateViews';
import { lockedCopy } from '@/features/content/accessCopy';
import { toUserMessage } from '@/lib/api';
import { applySession, keepInvite } from './session-cache';
import { cellCentre, isFigureBoard } from './spread-table';
import classes from './SpreadCanvas.module.css';
import { pluralize } from '@/lib/plural';
import {
  createSpreadSession,
  fetchSpread,
  SPREAD_SESSIONS_KEY,
  spreadKey,
  spreadSessionKey,
} from './api';
import { LockedOffer } from '@/features/access/PlanOffer';

/**
 * The booklet's own text for a spread: what it is for, how to draw, its named
 * blocks and — when the questions belong to the whole spread rather than to one
 * card — the questions.
 *
 * Built from the platform's own vocabulary: a bordered panel on the ivory
 * ground, `Title order={4}` for a block heading, plain paragraphs. No tinted
 * blocks, no rules down the side and no icons — a section icon here would say
 * «Игры», because that is what it says in the navigation.
 */
function SpreadGuidanceBlock({ guidance }: { guidance: SpreadGuidance }) {
  const { intent, draw, notes = [], questions = [] } = guidance;
  if (!intent && !draw && notes.length === 0 && questions.length === 0) return null;

  return (
    <Paper withBorder p="lg" radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
      <Stack gap="md">
        {intent ? <Text>{intent}</Text> : null}
        {draw ? <Text>{draw}</Text> : null}

        {notes.map((note) => (
          <Stack key={note.title ?? note.body} gap={6}>
            {note.title ? <Title order={4}>{note.title}</Title> : null}
            <Text>{note.body}</Text>
          </Stack>
        ))}

        {questions.length > 0 ? (
          <Stack gap={6}>
            <Title order={4}>Вопросы</Title>
            <QuestionList questions={questions} />
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
}

/**
 * Доска расклада как схема: фигуры на своих клетках, трогать нечего.
 *
 * Та же геометрия, что и на столе сессии, — центр клетки считается арифметикой
 * от стороны доски. Номера подписаны, чтобы список позиций под ней читался как
 * продолжение картинки, а не как отдельная таблица.
 */
function BoardPreview({ spread }: { spread: SpreadDetail }) {
  const grid = spread.boardGrid ?? 8;

  return (
    <Paper withBorder p="xs" radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
      <div
        className={classes.boardPreview}
        data-testid="spread-board-preview"
        style={
          {
            '--spread-board-image': `url(${spread.boardImage})`,
            '--spread-board-grid': grid,
          } as CSSProperties
        }
      >
        {spread.positions.map((position) =>
          position.marker && position.cell ? (
            <div
              key={position.id}
              className={classes.boardFigure}
              style={{
                left: `${cellCentre(position.cell.col, grid)}%`,
                top: `${cellCentre(position.cell.row, grid)}%`,
              }}
            >
              <Badge className={classes.boardFigureNumber} size="sm" variant="filled" color="teal">
                {position.positionIndex + 1}
              </Badge>
              <img
                className={classes.boardFigureImage}
                src={position.marker.image}
                alt={`${position.positionIndex + 1}. ${position.title}`}
              />
            </div>
          ) : null,
        )}
      </div>
    </Paper>
  );
}

/** Questions read as a list of quiet lines — the same device the journal uses. */
function QuestionList({ questions, size = 'md' }: { questions: string[]; size?: 'sm' | 'md' }) {
  return (
    <Stack gap={4}>
      {questions.map((question) => (
        <Text key={question} size={size} c="var(--text-muted)">
          {question}
        </Text>
      ))}
    </Stack>
  );
}

export function SpreadDetailPage() {
  const { spreadId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: spreadKey(spreadId),
    queryFn: () => fetchSpread(spreadId),
    enabled: Boolean(spreadId),
  });

  const create = useMutation({
    mutationFn: (mode: 'solo' | 'group') => createSpreadSession(spreadId, { mode }),
    onSuccess: (session) => {
      applySession(queryClient, spreadSessionKey(session.id), session, keepInvite);
      void queryClient.invalidateQueries({ queryKey: SPREAD_SESSIONS_KEY });
      void navigate(`/spreads/${session.spread.slug}/session/${session.id}`);
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });

  if (query.isLoading) return <LoadingState label="Загружаем сессию..." />;
  if (query.isError || !query.data) return <ErrorState onRetry={() => void query.refetch()} />;

  const spread = query.data;
  const locked = lockedCopy(spread.access);
  const allowsSolo = spread.mode === 'solo' || spread.mode === 'both';
  const allowsGroup = spread.mode === 'group' || spread.mode === 'both';

  return (
    <>
      <PageHeader
        eyebrow="МАК-сессии"
        title={spread.title}
        description={spread.description}
        actions={
          <Button component={Link} to="/spreads" variant="subtle" leftSection={<IconArrowLeft size={16} />}>
            К списку
          </Button>
        }
      />

      <Grid gutter="xl">
        <Grid.Col span={{ base: 12, md: 5 }}>
          {/*
            У расклада на фигурах вместо обложки стоит его собственная доска.

            Обложка показывает настроение, а доска — устройство: где стоят
            белые, где чёрные и что за чем закреплено. Для «Шахмат» это и есть
            содержание расклада, и увидеть его нужно до того, как начинать
            сессию, а не после.
          */}
          {isFigureBoard(spread) ? (
            <BoardPreview spread={spread} />
          ) : (
            <Paper withBorder style={{ overflow: 'hidden', borderColor: 'var(--border-subtle)' }}>
              <AssetImage
                src={spread.coverImage}
                alt={spread.title}
                ratio="7 / 6"
                fallbackLabel="Обложка расклада не загружена"
              />
            </Paper>
          )}
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 7 }}>
          <Stack gap="lg">
            <Group gap="xs">
              <Badge color={spread.layout === 'freeform' ? 'gold' : 'teal'}>
                {spread.layout === 'freeform' ? 'Свободный стол' : pluralize(spread.positionCount, 'позиция', 'позиции', 'позиций')}
              </Badge>
              <Badge variant="default">{SPREAD_MODE_LABELS[spread.mode]}</Badge>
            </Group>

            {locked ? (
              <Alert color="gold" icon={<IconLock size={18} />} title={locked.reason}>
                <Stack gap="sm">
                  {locked.hint ? <Text size="sm">{locked.hint}</Text> : null}
                  {!spread.access.allowed ? <LockedOffer access={spread.access} content={{ kind: 'spread', id: spread.id }} /> : null}
                </Stack>
              </Alert>
            ) : (
              <>
                {spread.guidance ? <SpreadGuidanceBlock guidance={spread.guidance} /> : null}

                {spread.layout === 'preset' ? (
                  <Stack gap="sm">
                    <Title order={3}>Карты расклада</Title>
                    {spread.positions.map((position) => (
                      <Paper
                        key={position.id}
                        withBorder
                        p="md"
                        radius="lg"
                        style={{ borderColor: 'var(--border-subtle)' }}
                      >
                        <Group gap="md" wrap="nowrap" align="flex-start">
                          {/*
                            Фигура позиции, если у расклада она есть. Вырезана с
                            фона, поэтому стоит прямо на странице; мягкая тень —
                            чтобы светлая фигура не терялась на светлом.
                          */}
                          {position.marker ? (
                            <img
                              src={position.marker.image}
                              alt={position.marker.label}
                              width={56}
                              height={56}
                              style={{
                                flex: 'none',
                                objectFit: 'contain',
                                filter: 'drop-shadow(0 1px 1px rgba(20, 49, 47, 0.3))',
                              }}
                            />
                          ) : null}
                          <Stack gap="xs">
                            <div>
                              {/* Колода над названием — та же связка «надзаголовок → заголовок», что в шапке страницы. */}
                              <Text size="xs" fw={600} tt="uppercase" c="var(--text-subtle)" lts="0.08em">
                                {position.deckTitle ?? 'Колода на выбор'}
                              </Text>
                              <Title order={4}>
                                {position.positionIndex + 1}. {position.title}
                              </Title>
                            </div>
                            {/* Значение позиции прозой — там, где инструкция не задаёт вопрос. */}
                            {position.hint ? <Text size="sm">{position.hint}</Text> : null}
                            {position.questions.length > 0 ? (
                              <QuestionList questions={position.questions} size="sm" />
                            ) : null}
                          </Stack>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Text c="var(--text-muted)">
                    Выберите любую доступную колоду, вытягивайте карты и располагайте их на рабочем столе перетаскиванием.
                  </Text>
                )}

                <Stack gap="sm">
                  <Title order={3}>Начать</Title>
                  {allowsSolo ? (
                    <Button
                      size="lg"
                      leftSection={<IconUser size={18} />}
                      loading={create.isPending && create.variables === 'solo'}
                      disabled={create.isPending}
                      onClick={() => create.mutate('solo')}
                    >
                      Индивидуальная сессия
                    </Button>
                  ) : null}
                  {allowsGroup ? (
                    <Button
                      size="lg"
                      variant="light"
                      leftSection={<IconUsers size={18} />}
                      loading={create.isPending && create.variables === 'group'}
                      disabled={create.isPending}
                      onClick={() => create.mutate('group')}
                    >
                      Создать групповую сессию
                    </Button>
                  ) : null}
                </Stack>
              </>
            )}
          </Stack>
        </Grid.Col>
      </Grid>
    </>
  );
}
